import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { Pencil, Trash2, Search } from "lucide-react";

export default function Editinvoice() {
  const location = useLocation();
  const navigate = useNavigate();

  const { currentinvoiceno } = location.state || {};

  const [customer, setCustomer] = useState("");
  const [customerid, setCustomerid] = useState("");
  const [paymenttype, setPaymentype] = useState("");
  const [duedate, setDuedate] = useState("");

  const [rows, setRows] = useState([
    { productId: "", productname: "", quantity: 1, price: 0, total: 0 }
  ]);

  // ================= Load Invoice =================

  useEffect(() => {
    if (currentinvoiceno) {
      getInvoice();
    }
  }, []);

  const getInvoice = () => {
    axios
      .get(
        "http://tntyping.com/Adminconsole/api/nutsinvoicelistbyid?invoiceno=" +
          currentinvoiceno
      )
      .then((response) => {
        const errorcode = response.data.errorCode;
        const result = response.data.result;

        if (errorcode === 2) {
          Swal.fire("No Records");
        }

        if (errorcode === 0) {
          setCustomer(result.customername);
          setCustomerid(result.customerid);
          setPaymentype(result.paymenttype);
          setDuedate(result.duedate);

          const items = response.data.items || [];

          if (items.length > 0) {
            const loadedRows = items.map((item) => ({
              productId: item.productid,
              productname: item.productname,
              quantity: Number(item.qty),
              price: Number(item.price),
              total: Number(item.qty) * Number(item.price)
            }));

            setRows(loadedRows);
          }
        }
      })
      .catch(() => {
        Swal.fire("Error loading invoice");
      });
  };

  // ================= Handle Change =================

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];

    updatedRows[index][field] = value;

    if (field === "quantity" || field === "price") {
      updatedRows[index].total =
        Number(updatedRows[index].quantity) * Number(updatedRows[index].price);
    }

    setRows(updatedRows);
  };

  // ================= Add Row =================

  const addRow = () => {
    setRows([
      ...rows,
      { productId: "", productname: "", quantity: 1, price: 0, total: 0 }
    ]);
  };

  // ================= Remove Row =================

  const removeRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);

    setRows(updatedRows);
  };

  // ================= Totals =================

  const subtotal = rows.reduce((sum, r) => sum + Number(r.total), 0);

  // ================= Update Invoice =================

  const updateInvoice = () => {
    Swal.fire({
      text: "Update Invoice?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "No",
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#d33",
      width: 320,
      padding: "1em",
      customClass: {
        popup: "rounded-xl text-sm"
      }

    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          invoiceno: currentinvoiceno,
          items: rows
        };

        axios
          .post(
            "http://tntyping.com/Adminconsole/api/nutsupdateinvoices",
            payload
          )
          .then((response) => {
            if (response.data.errorCode === 0) {
              Swal.fire({
                icon: "success",
                text: response.data.Message,
                timer: 1500,
                showConfirmButton: false,
                width: 320,
                padding: "1em",
                customClass: {
                  popup: "rounded-xl text-sm"
                }
              });
            } else {
              Swal.fire("Error", response.data.Message, "error");
            }
          })
          .catch(() => {
            Swal.fire("Error", "Server error occurred", "error");
          });
      }
    });
  };

  // ================= Navigate =================

  const goViewinvoice = () => {
    navigate("/invoices");
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-6">
      {/* Title */}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Edit Sales Invoice</h2>

        <button
          className="bg-green-600 text-white px-6 py-2 rounded-lg"
          onClick={goViewinvoice}
        >
          View Invoices
        </button>
      </div>

      {/* Header */}

      <div className="bg-white p-6 rounded-xl shadow grid md:grid-cols-3 gap-6">
        <div>
          <label className="text-sm text-gray-500">Customer</label>
          <div className="border rounded-lg px-3 py-2 bg-gray-50 min-h-[40px] flex items-center">
            {customer || "Loading..."}
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-500">Payment Type</label>
          <div className="border rounded-lg px-3 py-2 bg-gray-50 min-h-[40px] flex items-center">
            {paymenttype || "Loading..."}
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-500">Due Date</label>
          <div className="border rounded-lg px-3 py-2 bg-gray-50 min-h-[40px] flex items-center">
            {duedate || "Loading..."}
          </div>
        </div>
      </div>

      {/* Products Table */}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-4 text-left">Product</th>

              <th className="p-4 text-center">Qty</th>

              <th className="p-4 text-center">Price</th>

              <th className="p-4 text-center">Total</th>

              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="border-t">
                <td className="p-3">
                  <input
                    type="text"
                    value={row.productname}
                    onChange={(e) =>
                      handleChange(index, "productname", e.target.value)
                    }
                    className="border rounded-lg px-3 py-2 w-full"
                  />
                </td>

                <td className="p-3 text-center">
                  <input
                    type="number"
                    min="1"
                    value={row.quantity}
                    onChange={(e) =>
                      handleChange(index, "quantity", e.target.value)
                    }
                    className="border rounded-lg w-20 px-2 py-1 text-center"
                  />
                </td>

                <td className="p-3 text-center">
                  <input
                    type="number"
                    value={row.price}
                    onChange={(e) =>
                      handleChange(index, "price", e.target.value)
                    }
                    className="border rounded-lg w-24 px-2 py-1 text-center"
                  />
                </td>

                <td className="p-3 text-center font-semibold">₹ {row.total}</td>

                <td className="p-3 text-center">
                  <button
                    onClick={() => removeRow(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="p-4 border-t">
          <button onClick={addRow} className="text-blue-600 font-medium">
            + Add another item
          </button>
        </div>
      </div>

      {/* Totals */}

      <div className="flex justify-end">
        <div className="bg-white p-6 rounded-xl shadow w-80">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span>

            <span>₹{subtotal}</span>
          </div>

          <div className="border-t pt-3 flex justify-between font-bold text-lg">
            <span>Total</span>

            <span>₹{subtotal}</span>
          </div>

          <button
            onClick={updateInvoice}
            className="mt-4 w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
          >
            Update Invoice
          </button>
        </div>
      </div>
    </div>
  );
}
